import React, { useMemo, useState } from "react";
import CustomTable from "components/Table/CustomTable";
import { useModal } from "contexts/ModalProvider";
import { useToasts } from "utils/useHotToast";
import { BrandRecomSchema } from "model/contents/brand-recom.schema";
import useTableChangeHandler from "hooks/useTableChangeHandler";
import ToastNotification from "components/Toast/ToastNotification";
import check from "assets/icons/check-circle.svg";
import slash from "assets/icons/slash-circle.svg";
import { useDeleteBrandRecomHighlightMutation, useReorderBrandRecomHighlightMutation } from "services/contents/api";
import Image from "components/Common/Image";
import { getImageUrl, ImageSourceType } from "utils/imageUtils";

const BrandRecomHighlightTable = ({ data, onTableChange, isLoading, error }) => {
  const { modal } = useModal();
  const { addToast, removeToast } = useToasts();
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [deleteBrandRecomHighlight, { isLoading: isDeleting }] = useDeleteBrandRecomHighlightMutation();
  const [reorderBrandRecomHighlight, { isLoading: isReorder }] = useReorderBrandRecomHighlightMutation();

  const { handleSort, handleFilter, handleSearch, handlePageChange } = useTableChangeHandler(onTableChange);

  const columns = useMemo(
    () => [
      {
        header: "รายการ Brand Affiliate",
        accessor: "name",
        sortable: true,
        render: (item) => (
          <div className="brand-info d-flex align-items-center gap-3">
            <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
            <div>
              <div className="text-lg fw-semibold">{item.name}</div>
            </div>
          </div>
        ),
      },
      {
        header: "",
        accessor: "actions",
        width: 50,
        render: (item) => (
          <div
            className="text-gray-500 cursor-pointer"
            onClick={(event) => {
              event.stopPropagation();
              handleDeleteHighlight(item);
            }}
          >
            <i className="bx bx-trash"></i>
          </div>
        ),
      },
    ],
    []
  );

  const handleDeleteHighlight = async (item) => {
    const result = await modal.alert.danger({
      title: "ยืนยันที่จะนำสินค้าออกจากแบนด์ที่แนะนำ",
      message: `ตรวจสอบรายละเอียดแนะนำที่คุรต้องการนำออกจากระบบว่าถูกต้องหรือไม่?`,
      confirmText: "ยืนยันที่จะนำออก",
      cancelText: "ยกเลิก",
      children: (
        <div className="brand-info d-flex align-items-center gap-3">
          <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
          <div>
            <div className="text-lg fw-semibold">{item.name}</div>
          </div>
        </div>
      ),
    });
    if (result) {
      try {
        await deleteBrandRecomHighlight(item);
        const toastId = addToast(<ToastNotification variant="success" icon={check} title="นำแบรนด์ออกจากรายการไฮไลท์แล้ว" description={`แบรนด์ "${item.name}" ถูกนำออกจากรายการไฮไลท์เรียบร้อยแล้ว`} onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
        // Refresh data
        if (onTableChange) {
          onTableChange({});
        }
      } catch (error) {
        console.error("Failed to delete brand from highlight:", error);
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถลบแบรนด์ออกจากรายการไฮไลท์ได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleDeleteSelected = async (items) => {
    const result = await modal.alert.danger({
      title: "ยืนยันที่จะนำสินค้าออกจากแบนด์ที่แนะนำ",
      message: `คุณได้ทำการกดนำสินค้าออกจากแบนด์ที่แนะนำในระบบทั้งหมด “${items.length} รายการ”`,
      confirmText: "ยืนยันที่จะนำออก",
      cancelText: "ยกเลิก",
      children: (
        <div className="d-flex flex-column gap-3 divide-y">
          {items.map((item, index) => (
            <div key={item.id} className={`d-flex flex-column gap-2 ${index > 0 && "pt-3"}`}>
              <div className="brand-info d-flex align-items-center gap-3">
                <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
                <div>
                  <div className="text-lg fw-semibold">{item.name}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ),
    });

    if (result) {
      try {
        await deleteBrandRecomHighlight(items);
        const toastId = addToast(<ToastNotification variant="success" icon={check} title={`นำแบรนด์ออกจากรายการไฮไลท์แล้ว ${selectedBrands.length} รายการ`} description="แบรนด์ที่เลือกถูกนำออกจากรายการไฮไลท์เรียบร้อยแล้ว" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
        // Refresh data
        if (onTableChange) {
          onTableChange({});
        }
        // Clear selection
        setSelectedBrands([]);
      } catch (error) {
        console.error("Failed to delete brands from highlight:", error);
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถลบแบรนด์ออกจากรายการไฮไลท์ได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleSelectionChange = (selected) => {
    setSelectedBrands(selected);
  };

  const handleSortedChange = (sorted) => {
    const updateOrder = async () => {
      try {
        const { movement } = sorted;
        await reorderBrandRecomHighlight({
          id: movement.itemId,
          order: movement.newIndex,
          // oldIndex: movement.oldIndex,
          // newIndex: movement.newIndex,
        });

        const toastId = addToast(<ToastNotification variant="success" icon={check} title="อัพเดตลำดับแบรนด์สำเร็จ" description={`แบรนด์ "${movement.movedItem.name}" ถูกย้ายไปยังตำแหน่งใหม่เรียบร้อยแล้ว`} onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });

        // Refresh data
        if (onTableChange) {
          onTableChange({});
        }
      } catch (error) {
        console.error("Failed to update brand order:", error);
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถอัพเดตลำดับแบรนด์ได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
      }
    };

    updateOrder();
  };

  return (
    <div className="mt-4">
      <CustomTable
        keyField="id"
        data={data?.data || []}
        columns={columns}
        sortable={true}
        onSortedChange={handleSortedChange}
        selectable={true}
        onSelectionChange={handleSelectionChange}
        selectionActions={
          <a
            className="danger"
            onClick={() => {
              if (selectedBrands.length > 0) {
                handleDeleteSelected(selectedBrands);
              }
            }}
          >
            <i className="bx bx-trash" />
            <span className="d-none d-md-block">ลบรายการที่เลือกไว้</span>
          </a>
        }
        className="commission-table"
        itemsPerPage={data?.pagination?.limit || 10}
        totalItems={data?.pagination?.total || 0}
        onSort={handleSort}
        onFilter={handleFilter}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

const withYupValidation = (WrappedComponent, schema) => {
  return function WithYupValidation(props) {
    // Skip validation if loading or error
    if (props.isLoading || props.error) {
      return <WrappedComponent {...props} />;
    }

    try {
      schema.validateSync(props.data);
      return <WrappedComponent {...props} />;
    } catch (error) {
      return (
        <div className="alert alert-danger">
          <strong>Validation Error:</strong> {error.message}
        </div>
      );
    }
  };
};

const ValidatedTable = withYupValidation(BrandRecomHighlightTable, BrandRecomSchema);

export default ValidatedTable;
